<template>
  <div>
    <!-- Rejection banner -->
    <b-alert v-if="isRejected" variant="danger" show class="mb-3">
      <strong>Rejected:</strong> {{ timesheet.rejection_reason }}
      <span v-if="timesheet.rejection_comment">
        - {{ timesheet.rejection_comment }}</span
      >
    </b-alert>

    <!-- Submitted pending banner -->
    <b-alert v-if="isSubmittedPending" variant="warning" show class="mb-3">
      You're editing a submitted timesheet that is pending review. Saving will
      resubmit it for approval.
    </b-alert>

    <!-- Project + Week selector -->
    <b-card no-body class="mb-3">
      <b-card-body>
        <b-form-row>
          <b-col md="6">
            <b-form-group label="Project">
              <b-form-select
                v-model="selectedProjectId"
                :options="projectOptions"
                :disabled="isAmending"
                @change="onProjectSelected"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group :label="`Week starting (${weekStartDayName})`">
              <b-form-datepicker
                :key="weekPickerKey"
                :value="weekStart"
                :disabled="isAmending"
                locale="en-AU"
                :date-format-options="dateFormatOptions"
                today-button
                close-button
                show-decade-nav
                class="date-input week-start-input"
                @input="onWeekChange"
              />
            </b-form-group>
          </b-col>
        </b-form-row>
      </b-card-body>
    </b-card>

    <!-- Loading -->
    <div v-if="loadingSheet" class="text-center py-4">
      <b-spinner variant="primary" />
    </div>

    <template v-if="timesheet && !loadingSheet && !isLocked">
      <!-- Standard hours panel -->
      <b-card no-body class="mb-3">
        <b-card-header class="py-2 font-weight-bold"
          >Standard Hours</b-card-header
        >
        <b-card-body>
          <b-form-row class="align-items-end">
            <b-col sm="3">
              <b-form-group label="Start" class="mb-0">
                <datetime-input v-model="stdStart" type="time" />
              </b-form-group>
            </b-col>
            <b-col sm="3">
              <b-form-group label="Finish" class="mb-0">
                <datetime-input v-model="stdFinish" type="time" />
              </b-form-group>
            </b-col>
            <b-col sm="3">
              <b-form-group label="Break (min)" class="mb-0">
                <b-form-input
                  v-model.number="stdBreak"
                  type="number"
                  min="0"
                  step="5"
                />
              </b-form-group>
            </b-col>
          </b-form-row>
          <b-form-group label="Days" class="standard-hours-day-group mb-3">
            <div class="standard-hours-days">
              <b-button
                v-for="(label, di) in dayLabels"
                :key="label"
                size="sm"
                variant="light"
                class="day-toggle-btn"
                :class="{
                  'day-toggle-btn-active': selectedDays.includes(di),
                }"
                @click="toggleSelectedDay(di)"
              >
                {{ label }}
              </b-button>
            </div>
          </b-form-group>
          <div class="standard-hours-actions">
            <b-button
              variant="outline-primary"
              size="sm"
              :disabled="!selectedDays.length"
              @click="applyToDays(selectedDays)"
            >
              Apply to Selected Days
            </b-button>
            <b-button
              variant="outline-primary"
              size="sm"
              @click="applyToEntireWeek"
            >
              Apply to Entire Week
            </b-button>
            <b-button
              variant="outline-danger"
              size="sm"
              :disabled="!timesheet"
              @click="clearAll"
            >
              Clear All
            </b-button>
          </div>
        </b-card-body>
      </b-card>

      <!-- Worker checklist -->
      <b-card no-body class="mb-3">
        <b-card-header
          class="py-2 font-weight-bold d-flex align-items-center justify-content-between"
        >
          <span>Workers on Site</span>
          <div class="d-flex align-items-center" style="gap: 8px">
            <b-button
              variant="outline-primary"
              size="sm"
              :disabled="!tokenRows.length"
              @click="toggleSelectAllWorkers"
            >
              {{ allWorkersSelected ? 'Unselect All' : 'Select All' }}
            </b-button>
            <small class="text-muted">Show:</small>
            <b-form-select
              v-model="workerPageSize"
              :options="workerPageSizeOptions"
              size="sm"
              style="width: auto"
              @change="workerPage = 1"
            />
          </div>
        </b-card-header>
        <b-list-group flush>
          <b-list-group-item
            v-for="row in pagedTokenRows"
            :key="row.worker.id"
            class="py-2"
          >
            <b-form-checkbox
              :checked="row.on_site"
              @change="toggleWorker(row.worker.id, $event)"
            >
              <span class="font-weight-medium worker-name">{{
                workerDisplayName(row.worker)
              }}</span>
              <b-badge
                v-if="row.worker.pending"
                variant="warning"
                pill
                class="ml-1"
                style="font-size: 0.65rem"
                >Pending</b-badge
              >
              <span
                v-if="row.worker.trade"
                class="text-muted small ml-2 worker-trade"
                >{{ formatTrade(row.worker.trade) }}</span
              >
            </b-form-checkbox>
          </b-list-group-item>
        </b-list-group>
        <b-card-footer
          v-if="workerPageSize !== 0 && tokenRows.length > workerPageSize"
          class="py-2 d-flex align-items-center justify-content-between"
        >
          <small class="text-muted">
            {{ (workerPage - 1) * workerPageSize + 1 }}–{{
              Math.min(workerPage * workerPageSize, tokenRows.length)
            }}
            of {{ tokenRows.length }} workers
          </small>
          <b-pagination
            v-model="workerPage"
            :total-rows="tokenRows.length"
            :per-page="workerPageSize"
            size="sm"
            class="mb-0"
            hide-goto-end-buttons
          />
        </b-card-footer>
      </b-card>

      <!-- Weekly hours grid -->
      <b-card no-body class="mb-3 weekly-hours-card">
        <b-card-header class="weekly-hours-header">
          <div>
            <span>Weekly Hours</span>
            <small
              >Add the days worked, then enter start, finish and break.</small
            >
          </div>
          <div class="weekly-hours-header-actions">
            <b-button
              v-if="timesheet && selectedProjectId"
              variant="outline-primary"
              size="sm"
              @click="$bvModal.show('add-tradie-modal')"
            >
              <b-icon icon="person-plus" class="mr-1" />
              Add Tradie
            </b-button>
            <div class="weekly-hours-summary">
              <span>
                {{ activeWorkersCount }}
                {{ activeWorkersCount === 1 ? 'worker' : 'workers' }}
              </span>
              <span>
                {{ weeklyPresentDays }}
                {{ weeklyPresentDays === 1 ? 'day' : 'days' }} added
              </span>
            </div>
          </div>
        </b-card-header>
        <div class="px-3 pt-2">
          <b-form-input
            v-model="workerSearch"
            placeholder="Search workers..."
            size="sm"
            class="mb-2"
            style="max-width: 260px"
          />
        </div>
        <div class="table-responsive weekly-hours-scroll">
          <table class="table table-sm mb-0 hours-grid">
            <thead>
              <tr>
                <th class="worker-heading">Worker</th>
                <th
                  v-for="col in gridColumns"
                  :key="col.date"
                  class="day-heading text-center"
                >
                  <span class="day-label">{{ col.label }}</span>
                  <small>{{ col.dateStr }}</small>
                </th>
                <th class="total-heading text-center">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in tokenRows"
                :key="row.worker.id"
                :class="{ 'table-secondary': !row.on_site }"
              >
                <td class="worker-cell">
                  <div class="worker-name">
                    {{ workerDisplayName(row.worker) }}
                  </div>
                  <b-badge
                    v-if="row.worker.pending"
                    variant="warning"
                    pill
                    class="mt-1"
                    style="font-size: 0.65rem"
                    >Pending</b-badge
                  >
                  <div v-if="row.worker.trade" class="worker-trade">
                    {{ formatTrade(row.worker.trade) }}
                  </div>
                </td>
                <td
                  v-for="(day, di) in row.days"
                  :key="day.date"
                  class="day-cell"
                  :class="{
                    'day-cell-muted': !row.on_site,
                    'day-cell-active': row.on_site && day.present,
                    'day-cell-leave': row.on_site && isLeaveDay(day),
                    'day-cell-locked': row.on_site && isLockedLeaveDay(day),
                  }"
                  :data-label="gridColumns[di] ? gridColumns[di].label : ''"
                >
                  <template v-if="row.on_site">
                    <b-form-select
                      :value="day.leave_type"
                      :options="leaveTypeOptions"
                      size="sm"
                      class="leave-type-select"
                      :disabled="isLockedLeaveDay(day)"
                      :title="lockedLeaveReason(day)"
                      @change="setLeaveType(row.worker.id, di, $event)"
                    />
                    <div
                      v-if="isLockedLeaveDay(day)"
                      class="day-locked-note"
                      :title="lockedLeaveReason(day)"
                    >
                      <b-icon-lock-fill />
                      <span>{{ lockedLeaveLabel(day) }}</span>
                    </div>
                    <template v-if="!isLeaveDay(day)">
                      <div
                        class="day-availability"
                        :class="{ 'day-availability-active': day.present }"
                      >
                        <button
                          v-if="!day.present"
                          type="button"
                          class="day-add-btn"
                          :aria-label="`Add ${row.worker.full_name} ${gridColumns[di].label}`"
                          @click="setPresent(row.worker.id, di, true)"
                        >
                          <b-icon-plus />
                          <span>Add day</span>
                        </button>
                        <template v-else>
                          <span class="presence-pill">On site</span>
                          <button
                            type="button"
                            class="day-remove-btn"
                            :aria-label="`Remove ${row.worker.full_name} ${gridColumns[di].label}`"
                            @click="setPresent(row.worker.id, di, false)"
                          >
                            <b-icon-x />
                          </button>
                        </template>
                      </div>
                      <div v-if="day.present" class="day-fields">
                        <label class="day-field">
                          <span>Start</span>
                          <datetime-input
                            :value="day.start"
                            type="time"
                            size="sm"
                            @input="
                              setDayField(row.worker.id, di, 'start', $event)
                            "
                          />
                        </label>
                        <label class="day-field">
                          <span>Finish</span>
                          <datetime-input
                            :value="day.finish"
                            type="time"
                            size="sm"
                            @input="
                              setDayField(row.worker.id, di, 'finish', $event)
                            "
                          />
                        </label>
                        <label class="day-field">
                          <span>Break</span>
                          <b-form-input
                            :value="day.break_minutes"
                            type="number"
                            min="0"
                            step="5"
                            size="sm"
                            placeholder="Mins"
                            @input="
                              setDayField(
                                row.worker.id,
                                di,
                                'break_minutes',
                                Number($event),
                              )
                            "
                          />
                        </label>
                        <div v-if="dayHours(day)" class="day-hours">
                          {{ dayHours(day) }} worked
                          <span v-if="day.break_minutes">
                            · {{ day.break_minutes }}m brk
                          </span>
                        </div>
                        <div v-if="isOvernightDay(day)" class="day-overnight">
                          Overnight - ends next morning
                        </div>
                        <div v-if="dayError(day)" class="day-error">
                          {{ dayError(day) }}
                        </div>
                        <div
                          v-if="hasOtherProjectConflict(day)"
                          class="day-conflict"
                          :title="day.other_project_name"
                        >
                          Also clocked in at {{ day.other_project_name }}
                        </div>
                        <div
                          v-if="hasSupervisorHoursWarning(day)"
                          class="day-conflict"
                        >
                          Already clocked in at
                          {{
                            formatBusinessTime(
                              day.active_clock_in_at,
                              undefined,
                              timesheet.timezone,
                            )
                          }}
                        </div>
                      </div>
                    </template>
                    <div
                      v-else-if="!isLockedLeaveDay(day)"
                      class="leave-day-badge"
                    >
                      {{ leaveTypeLabel(day.leave_type) }}
                    </div>
                  </template>
                  <span v-else class="offsite-mark">-</span>
                </td>
                <td class="row-total-cell">
                  <span class="row-total-pill">{{ rowTotal(row) }}</span>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr style="background: #f8fafc; border-top: 2px solid #cbd5e1">
                <td class="worker-cell font-weight-bold" style="color: #0f172a">
                  Day totals
                </td>
                <td
                  v-for="(mins, i) in dayTotals"
                  :key="i"
                  class="text-center font-weight-bold day-total-cell"
                  style="padding: 0.7rem 0.55rem; color: #0f172a"
                  :data-label="gridColumns[i] ? gridColumns[i].label : ''"
                >
                  {{ formatMins(mins) }}
                </td>
                <td class="row-total-cell font-weight-bold">
                  <span
                    class="row-total-pill"
                    style="background: #dbeafe; color: #1d4ed8"
                  >
                    {{ formatMins(grandTotal) }}
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div class="px-3 pt-2">
          <supervisor-pending-workers-panel
            :workers="(timesheet && timesheet.pending_workers) || []"
          />
        </div>
      </b-card>

      <!-- Crew plant & equipment (sheet-level, opt-out via company setting) -->
      <b-card v-if="plantEntries.length || canAddPlant" no-body class="mb-3">
        <b-card-header
          class="py-2 font-weight-bold d-flex align-items-center justify-content-between"
        >
          <span>Plant &amp; Equipment</span>
          <div class="d-flex align-items-center crew-plant-heading-actions">
            <b-button
              v-if="canAddPlant"
              variant="outline-primary"
              size="sm"
              @click="openAddPlantModal"
            >
              + Add equipment
            </b-button>
          </div>
        </b-card-header>
        <b-card-body v-if="plantEntries.length">
          <supervisor-crew-plant-panel
            :entries="plantEntries"
            :editable="canAddPlant"
            :show-cost="false"
            @remove="removeEditPlantEntry"
          />
          <supervisor-crew-plant-cards
            :entries="plantEntries"
            :editable="canAddPlant"
            :show-cost="false"
            @remove="removeEditPlantEntry"
          />
        </b-card-body>
        <b-card-body v-else class="py-3 text-muted small">
          No equipment logged for this week yet.
        </b-card-body>
      </b-card>

      <!-- Photos -->
      <b-card no-body class="mb-3">
        <b-card-header class="py-2 font-weight-bold">Photos</b-card-header>
        <b-card-body>
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="uploadingPhotos"
            @click="launchPhotoPicker"
          >
            <template v-if="uploadingPhotos">
              <b-spinner small class="mr-1" />
              Uploading {{ pendingUploadCount }} photo(s)…
            </template>
            <template v-else>Add Photos</template>
          </b-button>
          <b-form-file
            ref="photoPicker"
            multiple
            accept="image/*"
            style="display: none"
            @input="onPhotosPicked"
          />
          <b-alert
            :show="failedFiles.length > 0"
            variant="danger"
            class="mt-3 mb-0 d-flex align-items-center justify-content-between flex-wrap"
          >
            <span>{{ failedFiles.length }} photo(s) failed to upload.</span>
            <span>
              <b-button
                size="sm"
                variant="outline-danger"
                class="mr-2"
                @click="uploadFiles(failedFiles)"
                >Retry</b-button
              >
              <b-button
                size="sm"
                variant="outline-secondary"
                @click="failedFiles = []"
                >Dismiss</b-button
              >
            </span>
          </b-alert>
          <div
            v-if="timesheet.photo_urls && timesheet.photo_urls.length"
            class="event-photos mt-3"
          >
            <div
              v-for="(photo, i) in timesheet.photo_urls"
              :key="photo.id"
              class="photo-thumb-wrap"
              :class="{ 'photo-thumb-deleting': deletingPhotoId === photo.id }"
            >
              <img
                :src="photo.url"
                class="event-photo-thumb"
                :alt="'Photo ' + (i + 1)"
                @click="openLightbox(timesheet.photo_urls, i)"
              />
              <b-spinner
                v-if="deletingPhotoId === photo.id"
                small
                class="remove-icon"
              />
              <b-icon-x-circle-fill
                v-else
                variant="dark"
                class="remove-icon"
                @click="removePhoto(photo.id)"
              ></b-icon-x-circle-fill>
            </div>
          </div>
          <span v-else class="text-muted small ml-2">No photos attached</span>
        </b-card-body>
      </b-card>

      <!-- Comment + declaration -->
      <b-card no-body class="mb-3">
        <b-card-body class="comment-declaration-body">
          <b-form-group label="Comments / Notes" class="mb-3">
            <b-form-textarea
              v-model="comment"
              rows="3"
              placeholder="e.g. John left site early Wednesday. Rain day Thursday."
            />
          </b-form-group>
          <b-form-checkbox v-model="declared" class="declaration-check">
            I confirm that the hours submitted are accurate and reflect the
            actual hours worked on site.
          </b-form-checkbox>
        </b-card-body>
      </b-card>

      <!-- Actions -->
      <div class="d-flex gap-2 mb-4 submit-form-actions">
        <!-- Save Draft only checks isSubmittedPending (not isAmendable): a
             rejected sheet can still be saved as a draft via PUT before
             resubmitting, but a submitted sheet can't (backend only allows
             draft/rejected on that endpoint) — so this stays narrower than
             the Submit/Resubmit gating below. -->
        <p v-if="autosaveError" class="form-inline-error mr-auto">
          {{ autosaveError }}
        </p>
        <p v-else-if="firstDayErrorMessage" class="form-inline-error mr-auto">
          {{ firstDayErrorMessage }}
        </p>
        <b-button
          v-if="!isSubmittedPending"
          variant="outline-secondary"
          :disabled="saving"
          @click="saveDraft"
        >
          <b-spinner v-if="saving" small class="mr-1" />
          Save Draft
        </b-button>
        <b-button
          v-if="!isAmendable"
          variant="primary"
          :disabled="!canSubmit || submitting"
          @click="submit"
        >
          <b-spinner v-if="submitting" small class="mr-1" />
          Submit for Approval
        </b-button>
        <b-button
          v-if="isAmendable"
          variant="warning"
          :disabled="!canSubmit || submitting"
          @click="resubmit"
        >
          <b-spinner v-if="submitting" small class="mr-1" />
          {{ isSubmittedPending ? 'Save & Resubmit' : 'Amend & Resubmit' }}
        </b-button>
      </div>
    </template>

    <div v-if="timesheet && !loadingSheet && isLocked" class="text-center py-5">
      <b-icon icon="lock-fill" font-scale="2.5" class="text-success mb-3" />
      <h5 class="font-weight-bold text-success mb-1">Timesheet Approved</h5>
      <p class="text-muted mb-0">
        This timesheet for the week of {{ weekStart }} has been approved and is
        locked. Contact your administrator if changes are needed.
      </p>
    </div>

    <!-- Empty prompt -->
    <div
      v-if="!timesheet && !loadingSheet && selectedProjectId"
      class="text-center py-5 text-muted"
    >
      <p>Unable to load timesheet. Please try again.</p>
      <b-button variant="outline-primary" size="sm" @click="loadTimesheet"
        >Retry</b-button
      >
    </div>
    <div
      v-if="!timesheet && !loadingSheet && !selectedProjectId"
      class="text-center py-5 text-muted"
    >
      <p>Select a project to begin.</p>
    </div>

    <b-modal
      id="add-tradie-modal"
      modal-class="timesheet-modal"
      title="Add Tradie to Company"
      :ok-disabled="
        !addTradieForm.name.trim() ||
        !addTradieForm.email.trim() ||
        addingTradie
      "
      ok-title="Invite"
      cancel-title="Cancel"
      @ok.prevent="submitAddTradie"
      @hidden="addTradieForm = { name: '', email: '' }"
    >
      <b-form-group label="Full name" label-for="add-tradie-name">
        <b-form-input
          id="add-tradie-name"
          v-model="addTradieForm.name"
          type="text"
          autocomplete="off"
        />
      </b-form-group>
      <b-form-group label="Email address" label-for="add-tradie-email">
        <b-form-input
          id="add-tradie-email"
          v-model="addTradieForm.email"
          type="email"
          autocomplete="off"
        />
      </b-form-group>
    </b-modal>

    <!-- Add crew equipment -->
    <add-crew-plant-modal
      modal-id="add-crew-plant-modal-submit"
      :week-dates="addPlantModalWeekDates"
      :equipment-options="crewEquipmentOptions"
      @save="handleAddPlantSave"
    />

    <!-- Photo lightbox -->
    <b-modal
      id="supervisor-submit-photo-lightbox-modal"
      size="xl"
      centered
      hide-header
      :hide-footer="lightboxPhotos.length <= 1"
      body-class="p-1 text-center bg-dark"
      footer-class="justify-content-center"
      @hidden="
        lightboxPhotos = []
        lightboxIndex = 0
      "
    >
      <img
        v-if="lightboxPhotos.length"
        :src="lightboxPhotos[lightboxIndex]"
        style="max-width: 100%; max-height: 80vh; object-fit: contain"
      />
      <template #modal-footer>
        <b-button variant="light" @click="lightboxPrev">&#8592; Prev</b-button>
        <span class="mx-3 text-white"
          >{{ lightboxIndex + 1 }} / {{ lightboxPhotos.length }}</span
        >
        <b-button variant="light" @click="lightboxNext">Next &#8594;</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import DatetimeInput from '@/components/timesheets/DatetimeInput.vue'
import AddCrewPlantModal from '@/components/timesheets/supervisor/AddCrewPlantModal.vue'
import SupervisorCrewPlantPanel from '@/components/timesheets/supervisor/SupervisorCrewPlantPanel.vue'
import SupervisorCrewPlantCards from '@/components/timesheets/supervisor/SupervisorCrewPlantCards.vue'
import SupervisorPendingWorkersPanel from '@/components/timesheets/supervisor/SupervisorPendingWorkersPanel.vue'
import {
  formatTrade,
  dayMinutes as minutesWorked,
  formatHours,
  isOvernight,
  dayError as sharedDayError,
  LEAVE_TYPE_OPTIONS,
  leaveTypeLabel,
  isLeaveDay,
  isLockedLeaveDay,
  patchUnlockedDay,
  buildPlantEntriesPayload,
  buildEquipmentOptions,
  formatBusinessTime,
  tokenRows as sharedTokenRows,
} from '@/utils/timesheetHelpers'
import dayjs from 'dayjs'
import { startDayOf } from '@/utils/weekHelpers'

// Indexed by JS getDay(): 0=Sun ... 6=Sat
const SHORT_DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// Grid indices (0-6, relative to weekStart) of Mon-Fri for a week
// beginning on startDay.
function weekdayIndices(startDay) {
  const indices = []
  for (let i = 0; i < 7; i++) {
    const wd = (startDay + i) % 7
    if (wd >= 1 && wd <= 5) indices.push(i)
  }
  return indices
}
const FULL_DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

export default {
  name: 'SupervisorSubmitForm',
  components: {
    DatetimeInput,
    AddCrewPlantModal,
    SupervisorCrewPlantPanel,
    SupervisorCrewPlantCards,
    SupervisorPendingWorkersPanel,
  },

  props: {
    amendSheet: { type: Object, default: null },
    startDay: { type: Number, default: 1 },
  },

  data() {
    return {
      projects: [],
      selectedProjectId: null,
      weekStart: startDayOf(null, this.startDay),
      // Bumped on every pick so the datepicker remounts and shows the snapped
      // week start even when it equals the current value (no prop change).
      weekPickerKey: 0,
      // en-AU default renders "29 August 2026"; force numeric dd/mm/yyyy.
      dateFormatOptions: { year: 'numeric', month: '2-digit', day: '2-digit' },
      timesheet: null,
      loadingSheet: false,
      saving: false,
      autosaveError: null,
      submitting: false,
      stdStart: '06:00',
      stdFinish: '16:00',
      stdBreak: 30,
      selectedDays: weekdayIndices(this.startDay),
      comment: '',
      declared: false,
      addTradieForm: { name: '', email: '' },
      addingTradie: false,
      workerSearch: '',
      // Day-of-week (0=Sun..6=Sat) of the loaded sheet's week_start. The API
      // snaps that to the project's pay-period boundary, so once a sheet is
      // loaded this — not the company-default `startDay` prop — drives the
      // week labels. null until the first sheet loads.
      sheetStartDay: null,
      originalTimesheet: null,
      originalComment: '',
      workerPage: 1,
      workerPageSize: 10,
      workerPageSizeOptions: [
        { value: 10, text: '10' },
        { value: 25, text: '25' },
        { value: 50, text: '50' },
        { value: 0, text: 'All' },
      ],
      uploadingPhotos: false,
      pendingUploadCount: 0,
      failedFiles: [],
      deletingPhotoId: null,
      lightboxPhotos: [],
      lightboxIndex: 0,
      // Detached working copy of the sheet's crew plant entries, like
      // timesheet.rows — additions/removals stage here and only reach the
      // API on Save/Submit, guarded by plantEntriesDirty so an untouched
      // save never sends a payload that would clear existing entries.
      plantEntries: [],
      plantEntriesDirty: false,
      addPlantModalWeekDates: [],
      crewEquipmentOptions: [],
    }
  },

  computed: {
    ...mapGetters('auth', ['hasPermission']),

    canManagePlant() {
      return this.hasPermission('timesheets.plant.manage')
    },

    // Company-wide opt-out, mirrors the API's own gate in _saveSheetPlant —
    // keep the control hidden here too so a disabled company never sees a
    // button that would silently no-op on save.
    crewEquipmentEnabled() {
      const settings = this.$store.getters['timesheet/timesheetSettings']
      return settings
        ? settings.supervisor_crew_equipment_enabled !== false
        : true
    },

    canAddPlant() {
      return this.canManagePlant && this.crewEquipmentEnabled
    },

    // Company default until a sheet loads, then that sheet's pay-period
    // boundary (which may be a per-project override).
    effectiveStartDay() {
      return this.sheetStartDay != null ? this.sheetStartDay : this.startDay
    },
    weekStartDayName() {
      return FULL_DAY_NAMES[this.effectiveStartDay] || 'Monday'
    },
    isAmending() {
      return !!this.amendSheet
    },
    isRejected() {
      return this.timesheet && this.timesheet.status === 'rejected'
    },
    isSubmittedPending() {
      return this.timesheet && this.timesheet.status === 'submitted'
    },
    isAmendable() {
      return this.isRejected || this.isSubmittedPending
    },
    isLocked() {
      return !!(this.timesheet && this.timesheet.status === 'approved')
    },
    canSubmit() {
      return this.declared && !!this.timesheet && !this.hasDayErrors
    },

    // Names the first blocking day. hasDayErrors scans every row, but the grid
    // renders a filtered, paginated subset — so without this the Submit button
    // can be disabled with the explanation rendered nowhere on screen.
    firstDayErrorMessage() {
      if (!this.timesheet || !this.timesheet.rows) return null
      for (const row of this.timesheet.rows) {
        if (!row.on_site) continue
        for (const day of row.days || []) {
          const err = this.dayError(day)
          if (err) {
            const who = (row.worker && row.worker.full_name) || 'A worker'
            return `${who}, ${day.date}: ${err}`
          }
        }
      }
      return null
    },

    // Any day failing the API's own rules blocks submit, so the user fixes it
    // here rather than getting a 422 back.
    hasDayErrors() {
      if (!this.timesheet || !this.timesheet.rows) return false
      return this.timesheet.rows.some(
        (row) =>
          row.on_site && (row.days || []).some((day) => this.dayError(day)),
      )
    },
    projectOptions() {
      return [
        { value: null, text: '- Select a project -', disabled: true },
        ...this.projects.map((p) => ({
          value: p.id,
          text: p.site_name ? `${p.name} · ${p.site_name}` : p.name,
        })),
      ]
    },
    dayLabels() {
      return Array.from(
        { length: 7 },
        (_, i) => SHORT_DAY_NAMES[(this.effectiveStartDay + i) % 7],
      )
    },
    gridColumns() {
      if (!this.weekStart) return []
      const base = dayjs(this.weekStart)
      return this.dayLabels.map((label, i) => {
        const d = base.add(i, 'day')
        return {
          label,
          date: d.format('YYYY-MM-DD'),
          dateStr: d.toDate().toLocaleDateString('en-AU', {
            day: 'numeric',
            month: 'short',
          }),
        }
      })
    },
    tokenRows() {
      if (!this.timesheet || !this.timesheet.rows) return []
      let rows = sharedTokenRows(this.timesheet)
      if (this.workerSearch.trim()) {
        const q = this.workerSearch.trim().toLowerCase()
        rows = rows.filter((row) =>
          (row.worker.full_name || '').toLowerCase().includes(q),
        )
      }
      return rows
    },
    pagedTokenRows() {
      if (this.workerPageSize === 0) return this.tokenRows
      const start = (this.workerPage - 1) * this.workerPageSize
      return this.tokenRows.slice(start, start + this.workerPageSize)
    },
    activeWorkersCount() {
      return this.tokenRows.filter((row) => row.on_site).length
    },
    allWorkersSelected() {
      return (
        this.tokenRows.length > 0 && this.tokenRows.every((row) => row.on_site)
      )
    },
    weeklyPresentDays() {
      return this.tokenRows.reduce((total, row) => {
        return (
          total + row.days.filter((day) => row.on_site && day.present).length
        )
      }, 0)
    },
    dayTotals() {
      const totals = Array(7).fill(0)
      this.tokenRows.forEach((row) => {
        if (!row.on_site) return
        row.days.forEach((day, i) => {
          totals[i] += minutesWorked(day)
        })
      })
      return totals
    },
    grandTotal() {
      return this.dayTotals.reduce((a, b) => a + b, 0)
    },
    isDirty() {
      if (!this.timesheet || !this.originalTimesheet) return false
      return (
        JSON.stringify(this.timesheet) !==
          JSON.stringify(this.originalTimesheet) ||
        this.comment !== this.originalComment ||
        this.plantEntriesDirty
      )
    },
    leaveTypeOptions() {
      return LEAVE_TYPE_OPTIONS
    },
  },

  async mounted() {
    // Independent requests — crewEquipmentEnabled is a reactive computed off
    // the settings getter, so it updates on its own whenever this resolves;
    // no need to block loadProjects() on it.
    await Promise.all([
      this.$store.dispatch('timesheet/getTimesheetSettings'),
      this.loadProjects(),
    ])
    if (this.amendSheet) {
      this.selectedProjectId = this.amendSheet.project_id
      this.weekStart = this.amendSheet.week_start
      // The sheet's own week_start is authoritative for this project's
      // pay-period boundary — drive the day labels and the parent's
      // "Week Starts On" display off it.
      this.syncStartDayFromWeek(this.amendSheet.week_start)
      // Deep-clone the sheet for local editing
      this.timesheet = JSON.parse(JSON.stringify(this.amendSheet))
      this._applyCleanSheet(this.timesheet)
    }
  },

  watch: {
    workerSearch() {
      this.workerPage = 1
    },
    startDay(newVal) {
      // In amend mode the sheet and its week are pinned to what's being
      // amended — a late settings/prop change must not re-snap or reload it.
      if (this.isAmending) return
      // Once a sheet is loaded its own week_start governs the boundary
      // (see syncStartDayFromWeek) — a late company-default change mustn't
      // re-snap the week or reload it.
      if (this.sheetStartDay != null) return
      // Anchor to mid-week (+3 days) so snapping to any start day
      // lands in the same week rather than drifting to the previous one.
      const d = new Date(this.weekStart + 'T00:00:00')
      d.setDate(d.getDate() + 3)
      const mid = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        '0',
      )}-${String(d.getDate()).padStart(2, '0')}`
      this.weekStart = startDayOf(mid, newVal)
      this.selectedDays = weekdayIndices(newVal)
      this.loadTimesheet()
    },
  },

  methods: {
    formatTrade,
    leaveTypeLabel,
    isLeaveDay,
    isLockedLeaveDay,
    formatBusinessTime,

    // Why the day is read-only, for the cell's tooltip. Mirrors the two
    // messages _validateRowDays() returns so the supervisor reads the same
    // reason here that they would have hit at submit time.
    lockedLeaveReason(day) {
      if (!isLockedLeaveDay(day)) return null
      return day.leave_source === 'business_admin'
        ? 'Set by a business admin - contact them to change it.'
        : 'Part of a multi-day leave block - change it there.'
    },

    lockedLeaveLabel(day) {
      return day.leave_source === 'business_admin'
        ? 'Admin managed'
        : 'Multi-day leave'
    },

    formatMins(mins) {
      if (mins <= 0) return '-'
      const h = Math.floor(mins / 60)
      const m = mins % 60
      return m ? `${h}h ${m}m` : `${h}h`
    },

    workerDisplayName(worker) {
      if (!worker) return 'Unknown worker'
      return (
        (worker.full_name || '').trim() ||
        (worker.email || '').trim() ||
        (worker.user && (worker.user.email || '').trim()) ||
        'Unknown worker'
      )
    },

    async loadProjects() {
      try {
        this.projects = await this.$store.dispatch(
          'timesheet/supervisorFetchProjects',
        )
      } catch {
        this.$bvToast.toast('Failed to load projects.', {
          variant: 'danger',
          title: 'Error',
        })
      }
    },

    async loadTimesheet() {
      // Not draft-only: the API accepts saves on draft/submitted/rejected
      // sheets alike (see supervisorSaveDraft's PUT), and a rejected sheet
      // being amended can go dirty just like a draft. Restricting this to
      // 'draft' silently discarded in-progress amend edits whenever
      // anything else in this method (project/week switch, or the Add
      // Tradie flow below) triggered a reload.
      if (
        this.timesheet &&
        ['draft', 'submitted', 'rejected'].includes(this.timesheet.status) &&
        this.isDirty
      ) {
        await this._saveDraftSilently()
      }
      if (!this.selectedProjectId || !this.weekStart) return
      this.loadingSheet = true
      this.timesheet = null
      try {
        const sheet = await this.$store.dispatch(
          'timesheet/supervisorFetchTimesheet',
          {
            projectId: this.selectedProjectId,
            weekStart: this.weekStart,
          },
        )
        this.timesheet = sheet
        // A failed silent autosave (e.g. the previous week's edits tripped
        // the future-date guard) leaves autosaveError set. That error
        // describes the sheet we just navigated away from, not this newly
        // loaded one — carrying it forward makes it look like the CURRENT
        // week/date is invalid even after switching to a valid one.
        this.autosaveError = null
        // The API snaps the requested date to this project's pay-period
        // boundary (its own override, or the company default) — follow the
        // sheet it actually returned, for both the date and the day labels.
        if (sheet.week_start && sheet.week_start !== this.weekStart) {
          this.weekStart = sheet.week_start
        }
        this.syncStartDayFromWeek(sheet.week_start)
        this._applyCleanSheet(sheet)
      } catch {
        this.$bvToast.toast('Failed to load timesheet.', {
          variant: 'danger',
          title: 'Error',
        })
      } finally {
        this.loadingSheet = false
      }
    },

    onProjectSelected() {
      // Tell the parent which project is active (for the "Week Starts On"
      // hint), then load — loadTimesheet()'s response carries this project's
      // pay-period week_start, which syncStartDayFromWeek() applies to the
      // day labels and reports up.
      this.$emit('project-changed', this.selectedProjectId)
      this.loadTimesheet()
    },

    // The API returns each sheet already snapped to its project's pay-period
    // boundary, so its week_start is the source of truth for the start day —
    // more reliable here than the BA-only pay-period settings endpoint, which
    // a supervisor can't read. Mirrors it into the day labels and the
    // Standard Hours weekday default, and reports it to the parent.
    syncStartDayFromWeek(weekStartStr) {
      if (!weekStartStr) return
      const dow = dayjs(weekStartStr).day()
      if (this.sheetStartDay !== dow) {
        this.sheetStartDay = dow
        this.selectedDays = weekdayIndices(dow)
      }
      this.$emit('start-day', dow)
    },

    onWeekChange(val) {
      this.weekStart = startDayOf(val, this.effectiveStartDay)
      this.weekPickerKey++
      this.loadTimesheet()
    },

    // Unticking only flips on_site. The entered hours stay on the row so a
    // re-tick restores the week. Off-site rows are blanked at save time in
    // _buildRows() and ignored by the grid, totals and validation.
    toggleWorker(workerId, value) {
      const rows = JSON.parse(JSON.stringify(this.timesheet.rows))
      const rowIndex = rows.findIndex((r) => r.worker.id === workerId)
      if (rowIndex === -1) return
      rows[rowIndex].on_site = value
      this.$set(this.timesheet, 'rows', rows)
    },

    // Applies to the currently filtered worker list (tokenRows), not just
    // the paginated page, so a search + select-all behaves as expected.
    // Hours are kept on untick, same as toggleWorker.
    toggleSelectAllWorkers() {
      const targetValue = !this.allWorkersSelected
      const visibleIds = new Set(this.tokenRows.map((row) => row.worker.id))
      const rows = JSON.parse(JSON.stringify(this.timesheet.rows))
      rows.forEach((row) => {
        if (!visibleIds.has(row.worker.id)) return
        row.on_site = targetValue
      })
      this.$set(this.timesheet, 'rows', rows)
    },

    setPresent(workerId, di, value) {
      const rows = JSON.parse(JSON.stringify(this.timesheet.rows))
      const ri = rows.findIndex((r) => r.worker.id === workerId)
      if (ri === -1) return
      if (isLockedLeaveDay(rows[ri].days[di])) return
      rows[ri].days[di].present = value
      if (!value) {
        rows[ri].days[di].start = null
        rows[ri].days[di].finish = null
        rows[ri].days[di].break_minutes = 0
      }
      this.$set(this.timesheet, 'rows', rows)
    },

    setDayField(workerId, di, field, value) {
      const rows = JSON.parse(JSON.stringify(this.timesheet.rows))
      const ri = rows.findIndex((r) => r.worker.id === workerId)
      if (ri === -1) return
      if (isLockedLeaveDay(rows[ri].days[di])) return
      rows[ri].days[di][field] = value
      this.$set(this.timesheet, 'rows', rows)
    },

    // A leave day replaces the worked flow entirely — start/finish/break are
    // cleared and stay uneditable while it's set. Supervisors never see or
    // enter leave hours; that's BA-only on the other endpoint/screen.
    setLeaveType(workerId, di, value) {
      const rows = JSON.parse(JSON.stringify(this.timesheet.rows))
      const ri = rows.findIndex((r) => r.worker.id === workerId)
      if (ri === -1) return
      if (isLockedLeaveDay(rows[ri].days[di])) return
      rows[ri].days[di].leave_type = value || null
      if (value) {
        rows[ri].days[di].present = false
        rows[ri].days[di].start = null
        rows[ri].days[di].finish = null
        rows[ri].days[di].break_minutes = 0
      }
      this.$set(this.timesheet, 'rows', rows)
    },

    toggleSelectedDay(dayIndex) {
      const days = this.selectedDays.includes(dayIndex)
        ? this.selectedDays.filter((d) => d !== dayIndex)
        : [...this.selectedDays, dayIndex]
      this.selectedDays = days
    },

    async applyToDays(dayIndices) {
      if (!this.stdStart || !this.stdFinish || !dayIndices.length) return

      const wouldOverwrite = this.tokenRows.some(
        (row) =>
          row.on_site &&
          dayIndices.some((i) => row.days[i] && row.days[i].present),
      )

      if (wouldOverwrite) {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          'Some workers already have hours set for the selected days. Applying will overwrite them.',
          {
            title: 'Overwrite existing hours?',
            okTitle: 'Overwrite',
            cancelTitle: 'Cancel',
            okVariant: 'warning',
            centered: true,
            modalClass: 'confirm-modal',
          },
        )
        if (!confirmed) return
      }

      const rows = JSON.parse(JSON.stringify(this.timesheet.rows))
      for (const row of rows) {
        if (!row.on_site) continue
        for (const i of dayIndices) {
          if (row.days[i]) {
            row.days[i] = patchUnlockedDay(row.days[i], {
              present: true,
              start: this.stdStart,
              finish: this.stdFinish,
              break_minutes: this.stdBreak,
              leave_type: null,
            })
          }
        }
      }
      this.$set(this.timesheet, 'rows', rows)
    },

    applyToEntireWeek() {
      this.applyToDays([0, 1, 2, 3, 4, 5, 6])
    },

    async clearAll() {
      if (!this.timesheet) return
      const confirmed = await this.$bvModal.msgBoxConfirm(
        "This clears every worker's hours and unchecks everyone, giving you a blank timesheet. Leave set by a business admin stays put.",
        {
          title: 'Clear all hours?',
          okTitle: 'Clear all',
          cancelTitle: 'Cancel',
          okVariant: 'danger',
          centered: true,
          modalClass: 'confirm-modal',
        },
      )
      if (!confirmed) return
      const rows = this.timesheet.rows.map((row) => ({
        ...row,
        on_site: false,
        days: row.days.map((d) =>
          patchUnlockedDay(d, {
            present: false,
            start: null,
            finish: null,
            break_minutes: 0,
            leave_type: null,
          }),
        ),
      }))
      this.$set(this.timesheet, 'rows', rows)
      this.declared = false
    },

    rowTotal(row) {
      if (!row.on_site) return '-'
      const mins = row.days.reduce((sum, d) => sum + minutesWorked(d), 0)
      return mins > 0 ? formatHours(mins) : '-'
    },

    dayHours(day) {
      return formatHours(minutesWorked(day)) || null
    },

    // A finish earlier than the start is legitimate — the shift ends the next
    // morning. Surfaced so a genuine typo still stands out.
    isOvernightDay(day) {
      return !!day && day.present && isOvernight(day.start, day.finish)
    },

    // Mirrors the API's _validateRowDays() so the user gets inline feedback
    // instead of a 422 on save.
    dayError(day) {
      return sharedDayError(day)
    },

    // Informational only — a worker can legitimately be booked on more than
    // one project the same day, so this never blocks save/submit.
    hasOtherProjectConflict(day) {
      return !!day && day.present && !!day.other_project_conflict
    },

    // Reverse of hasOtherProjectConflict — the tradie has an open mobile
    // clock-in for this day that this supervisor/BA entry didn't come from.
    // Informational only, never blocks save/submit.
    hasSupervisorHoursWarning(day) {
      return !!day && day.present && !!day.active_clock_in_conflict
    },

    openAddPlantModal() {
      this.addPlantModalWeekDates = this.gridColumns.map((col) => ({
        iso: col.date,
        label: col.label.toUpperCase(),
        dayNum: new Date(col.date + 'T00:00:00').getDate(),
      }))
      this.fetchCrewEquipmentOptions(this.selectedProjectId)
      this.$bvModal.show('add-crew-plant-modal-submit')
    },

    async fetchCrewEquipmentOptions(projectId) {
      if (!projectId) {
        this.crewEquipmentOptions = []
        return
      }
      try {
        await this.$store.dispatch('plantEquipment/getMyPlantEquipment', {
          project_id: projectId,
        })
        const items = this.$store.getters['plantEquipment/items']
        this.crewEquipmentOptions = buildEquipmentOptions(items)
      } catch (e) {
        this.crewEquipmentOptions = []
      }
    },

    // New rows have no id yet and no server-computed rate/claim fields —
    // SupervisorCrewPlantPanel/Cards render a "pending save" placeholder for
    // those until the next successful save round-trips the real values.
    handleAddPlantSave({ date, entries }) {
      if (!entries.length) return
      const equipmentById = new Map(
        this.$store.getters['plantEquipment/items'].map((i) => [i.id, i]),
      )
      entries.forEach((e) => {
        const item = equipmentById.get(e.plant_equipment_id)
        this.plantEntries.push({
          id: null,
          date,
          plant_equipment_id: e.plant_equipment_id,
          plant_name: item ? item.name : '',
          plant_id_rego: item ? item.plant_id_rego : null,
          quantity: e.quantity,
          rate_snapshot: null,
          rate_type_snapshot: item ? item.rate_type : null,
          claim_value: null,
          is_amended: false,
          amendment_count: 0,
        })
      })
      this.plantEntriesDirty = true
    },

    removeEditPlantEntry(pe) {
      const idx = this.plantEntries.indexOf(pe)
      if (idx !== -1) {
        this.plantEntries.splice(idx, 1)
        this.plantEntriesDirty = true
      }
    },

    // Resets the "clean" snapshot fields (everything isDirty diffs against)
    // from a freshly fetched/saved sheet. Does NOT touch this.timesheet —
    // callers set that separately, immediately before or after calling this.
    _applyCleanSheet(sheet) {
      this.originalTimesheet = JSON.parse(JSON.stringify(sheet))
      this.comment = sheet.supervisor_comment || ''
      this.originalComment = this.comment
      this.plantEntries = JSON.parse(JSON.stringify(sheet.plant_entries || []))
      this.plantEntriesDirty = false
    },

    // Off-site rows keep their hours in local state (so a re-tick restores
    // them) but are sent blank, except locked leave days which pass through
    // unchanged. Mirrors the app's WorkerWeekRow.toJson.
    _buildRows() {
      return this.timesheet.rows.map((row) => ({
        worker: { id: row.worker.id },
        on_site: row.on_site,
        days: row.days.map((d) => {
          if (!row.on_site && !isLockedLeaveDay(d)) {
            return {
              date: d.date,
              start: null,
              finish: null,
              break_minutes: 0,
              present: false,
              leave_type: null,
            }
          }
          return {
            date: d.date,
            start: d.start || null,
            finish: d.finish || null,
            break_minutes: d.break_minutes || 0,
            present: d.present,
            leave_type: d.leave_type || null,
          }
        }),
      }))
    },

    // A 422 from _validateRowDays carries per-day messages keyed
    // rows.N.days.M.field. Showing the first is far more use than "failed".
    _firstValidationMessage(error) {
      const errors =
        error && error.response && error.response.data
          ? error.response.data.errors
          : null
      if (!errors) return null
      const first = Object.values(errors)[0]
      return Array.isArray(first) ? first[0] : first || null
    },

    async _saveDraftSilently() {
      if (!this.timesheet || !this.timesheet.id) return
      try {
        const updated = await this.$store.dispatch(
          'timesheet/supervisorSaveDraft',
          {
            id: this.timesheet.id,
            rows: this._buildRows(),
            comment: this.comment,
            plantEntries: this.plantEntriesDirty
              ? buildPlantEntriesPayload(this.plantEntries)
              : undefined,
          },
        )
        if (updated) {
          this.originalTimesheet = JSON.parse(JSON.stringify(updated))
          this.autosaveError = null
        }
      } catch (e) {
        // Autosave fires on project/week switch, so a swallowed failure loses
        // work the supervisor believes is saved. Surface it — quietly, since
        // this is a background save, but never silently.
        this.autosaveError =
          this._firstValidationMessage(e) || 'Draft not saved.'
      }
    },

    async saveDraft() {
      this.saving = true
      try {
        const updated = await this.$store.dispatch(
          'timesheet/supervisorSaveDraft',
          {
            id: this.timesheet.id,
            rows: this._buildRows(),
            comment: this.comment,
            plantEntries: this.plantEntriesDirty
              ? buildPlantEntriesPayload(this.plantEntries)
              : undefined,
          },
        )
        this.timesheet = updated
        this.plantEntries = JSON.parse(
          JSON.stringify(updated.plant_entries || []),
        )
        this.plantEntriesDirty = false
        this.$bvToast.toast('Draft saved.', {
          variant: 'success',
          title: 'Saved',
        })
      } catch (e) {
        this.$bvToast.toast(
          this._firstValidationMessage(e) || 'Failed to save draft.',
          { variant: 'danger', title: 'Error' },
        )
      } finally {
        this.saving = false
      }
    },

    async submit() {
      this.submitting = true
      try {
        await this.$store.dispatch('timesheet/supervisorSubmit', {
          id: this.timesheet.id,
          rows: this._buildRows(),
          comment: this.comment,
          plantEntries: this.plantEntriesDirty
            ? buildPlantEntriesPayload(this.plantEntries)
            : undefined,
        })
        this.$bvToast.toast('Timesheet submitted for approval.', {
          variant: 'success',
          title: 'Submitted',
        })
        this.$emit('submitted')
      } catch (e) {
        // A 422 from _validateRowDays carries the per-day reason. Swallowing
        // it left the supervisor with "Failed to submit timesheet." and no
        // idea which day or why.
        this.$bvToast.toast(
          this._firstValidationMessage(e) || 'Failed to submit timesheet.',
          {
            variant: 'danger',
            title: 'Error',
          },
        )
      } finally {
        this.submitting = false
      }
    },

    async resubmit() {
      this.submitting = true
      try {
        await this.$store.dispatch('timesheet/supervisorResubmit', {
          id: this.timesheet.id,
          rows: this._buildRows(),
          comment: this.comment,
          plantEntries: this.plantEntriesDirty
            ? buildPlantEntriesPayload(this.plantEntries)
            : undefined,
        })
        this.$bvToast.toast('Timesheet resubmitted for approval.', {
          variant: 'success',
          title: 'Resubmitted',
        })
        this.$emit('submitted')
      } catch (e) {
        this.$bvToast.toast(
          this._firstValidationMessage(e) || 'Failed to resubmit timesheet.',
          {
            variant: 'danger',
            title: 'Error',
          },
        )
      } finally {
        this.submitting = false
      }
    },

    launchPhotoPicker() {
      this.$refs.photoPicker.$el.childNodes[0].click()
    },

    onPhotosPicked(files) {
      const picked = Array.isArray(files) ? files : files ? [files] : []
      if (!picked.length) return
      // Clear the native input so re-picking the same file re-fires @input.
      if (this.$refs.photoPicker) {
        this.$refs.photoPicker.$el.childNodes[0].value = ''
      }
      const valid = []
      const rejected = []
      picked.forEach((file) => {
        if (file.type === 'image/gif') {
          rejected.push(`${file.name} (GIFs not allowed)`)
        } else if (!file.type || !file.type.startsWith('image/')) {
          rejected.push(`${file.name} (Not an image)`)
        } else if (file.size > 10 * 1024 * 1024) {
          rejected.push(`${file.name} (Larger than 10MB)`)
        } else {
          valid.push(file)
        }
      })
      if (rejected.length) {
        this.$bvToast.toast(`Rejected: ${rejected.join(', ')}`, {
          variant: 'danger',
          title: 'Error',
        })
      }
      if (valid.length) this.uploadFiles(valid)
    },

    async uploadFiles(files) {
      if (!files.length || !this.timesheet || !this.timesheet.id) return
      const sheetId = this.timesheet.id
      this.uploadingPhotos = true
      this.pendingUploadCount = files.length
      this.failedFiles = this.failedFiles.filter((f) => !files.includes(f))
      try {
        const urls = await this.$store.dispatch(
          'timesheet/supervisorUploadPhotos',
          { id: sheetId, files },
        )
        if (!this.timesheet || this.timesheet.id !== sheetId) return
        this.applyPhotoUrls(urls)
        this.$bvToast.toast(`${files.length} photo(s) uploaded.`, {
          variant: 'success',
          title: 'Uploaded',
        })
      } catch {
        this.failedFiles = [...this.failedFiles, ...files]
        this.$bvToast.toast('Failed to upload photos.', {
          variant: 'danger',
          title: 'Error',
        })
      } finally {
        this.uploadingPhotos = false
        this.pendingUploadCount = 0
      }
    },

    async removePhoto(mediaId) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Remove this photo?',
        {
          title: 'Remove photo?',
          okTitle: 'Remove',
          cancelTitle: 'Cancel',
          okVariant: 'danger',
          centered: true,
          modalClass: 'confirm-modal',
        },
      )
      if (!confirmed) return
      if (!this.timesheet || !this.timesheet.id) return
      const sheetId = this.timesheet.id
      this.deletingPhotoId = mediaId
      try {
        const urls = await this.$store.dispatch(
          'timesheet/supervisorDeletePhoto',
          { id: sheetId, mediaId },
        )
        if (!this.timesheet || this.timesheet.id !== sheetId) return
        this.applyPhotoUrls(urls)
      } catch {
        this.$bvToast.toast('Failed to remove photo.', {
          variant: 'danger',
          title: 'Error',
        })
      } finally {
        this.deletingPhotoId = null
      }
    },

    // All photo_urls writes funnel through here: mirror into
    // originalTimesheet so the isDirty JSON comparison stays equal and
    // project/week switches don't trigger a spurious _saveDraftSilently.
    applyPhotoUrls(urls) {
      this.$set(this.timesheet, 'photo_urls', urls)
      if (this.originalTimesheet) {
        this.$set(
          this.originalTimesheet,
          'photo_urls',
          JSON.parse(JSON.stringify(urls)),
        )
      }
    },

    openLightbox(photos, index) {
      this.lightboxPhotos = photos.map((p) => p.url)
      this.lightboxIndex = index
      this.$bvModal.show('supervisor-submit-photo-lightbox-modal')
    },
    lightboxPrev() {
      this.lightboxIndex =
        (this.lightboxIndex - 1 + this.lightboxPhotos.length) %
        this.lightboxPhotos.length
    },
    lightboxNext() {
      this.lightboxIndex = (this.lightboxIndex + 1) % this.lightboxPhotos.length
    },

    async submitAddTradie(bvModalEvent) {
      bvModalEvent.preventDefault()
      this.addingTradie = true
      try {
        const worker = await this.$store.dispatch(
          'timesheet/supervisorAddWorker',
          {
            projectId: this.selectedProjectId,
            name: this.addTradieForm.name.trim(),
            email: this.addTradieForm.email.trim(),
          },
        )
        this.$bvModal.hide('add-tradie-modal')
        const name = worker.full_name || this.addTradieForm.name
        // On a free-tier/trial company every project tradie is eligible
        // for a row regardless of tokens, so they show up on reload with
        // no further action. Off free tier, a row only seeds once a token
        // is assigned (see _eligibleRowUserIds) — telling the supervisor
        // that up front, right when they're looking at the confirmation,
        // is more useful than letting them discover it as a silent no-show
        // and only find the "awaiting a token" badge if they scroll to it.
        const message =
          this.timesheet && this.timesheet.grants_free_access
            ? `${name} has been invited and added to this project.`
            : `${name} has been invited, but won't appear on this timesheet until your admin assigns them a token.`
        this.$bvToast.toast(message, {
          variant: 'success',
          title: 'Tradie invited',
          autoHideDelay: 8000,
        })
        this.addTradieForm = { name: '', email: '' }
        await this.loadTimesheet()
      } catch (e) {
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          'Failed to invite tradie.'
        this.$bvToast.toast(msg, { variant: 'danger', title: 'Error' })
      } finally {
        this.addingTradie = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/layouts/timesheet-shared';
@import '~@/assets/scss/layouts/supervisor-worker-shared';

.crew-plant-heading-actions {
  gap: 10px;
}

.week-start-input {
  width: 100%;
}

/* b-form-datepicker's two-class .b-form-btn-label-control.form-control rule
   beats the app-wide one-class `.form-control { height: 50px }` on
   specificity, so it renders shorter than the sibling Project select.
   Match it back (same fix as SupervisorSubmitHistory's .date-input). */
/deep/ .week-start-input.form-control {
  height: 50px;
}

/* b-form-datepicker's inner value <label> carries class="form-control", so
   Bootstrap's base `.form-control { width: 100%; background: #fff }` makes it
   overrun the flex row — and the wrapper's rounded border — to the right of
   the calendar button. Let it share the row and stay transparent so only the
   wrapper's border/background shows. */
/deep/ .week-start-input > label {
  width: auto;
  min-width: 0;
  flex: 1 1 auto;
  height: auto;
  background-color: transparent;
  border: 0;
  border-radius: 0;
}

/* Phones: worker name + trades flowed as one inline stream, breaking names
   mid-word and crowding long trade lists at 320px. Keep the name on its own
   line and drop the trades to a wrapped line below it. */
@media (max-width: 575.98px) {
  .worker-name {
    display: inline-block;
  }

  .worker-trade {
    display: block;
    margin-left: 0 !important;
    margin-top: 2px;
    line-height: 1.35;
    word-break: break-word;
  }
}

.standard-hours-day-group /deep/ legend {
  margin-bottom: 0.45rem;
  color: #334155;
  font-size: 0.78rem;
  font-weight: 700;
}

.standard-hours-days {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.day-toggle-btn {
  min-width: 58px;
  min-height: 36px;
  padding: 0.4rem 0.85rem;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  background: #ffffff;
  color: #475569;
  font-size: 0.78rem;
  font-weight: 800;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: background-color 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease, color 0.15s ease, transform 0.15s ease;
}

.day-toggle-btn:hover,
.day-toggle-btn:focus {
  border-color: #9dbcf7;
  background: #f0f5ff;
  color: #006cff;
  box-shadow: 0 0 0 3px rgba(0, 108, 255, 0.12);
}

.day-toggle-btn-active,
.day-toggle-btn-active:hover,
.day-toggle-btn-active:focus {
  border-color: #006cff;
  background: linear-gradient(180deg, #2d86ff 0%, #006cff 100%);
  color: #ffffff;
  box-shadow: 0 6px 14px rgba(0, 108, 255, 0.22);
}

.day-toggle-btn:active {
  transform: translateY(1px);
}

.standard-hours-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.55rem;
  padding-top: 0.15rem;
}

.standard-hours-actions .btn {
  min-height: 38px;
  padding: 0.5rem 0.85rem;
  border-radius: 10px;
  font-weight: 700;
  white-space: nowrap;
}

.weekly-hours-card {
  border: 1px solid #dbe3ef;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.weekly-hours-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.15rem;
  border-bottom: 1px solid #dbe3ef;
  background: linear-gradient(135deg, #f8fbff 0%, #eef6ff 100%);
  color: #0f172a;
  font-weight: 700;
}

.weekly-hours-header small {
  display: block;
  margin-top: 0.2rem;
  color: #6b7280;
  font-size: 0.78rem;
  font-weight: 500;
}

/* Add Tradie + the summary pills as one right-aligned group. Previously
   these were separate flex children of the header alongside the title, so
   space-between spread all three evenly and stranded the button in the
   middle of the row instead of next to the pills it belongs with. */
.weekly-hours-header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

.weekly-hours-summary {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.4rem;
  flex-shrink: 0;
}

/* Phones: the three header parts (title + description, Add Tradie button,
   summary pills) don't fit side by side — the description wraps word-by-word
   in a sliver and the squeezed button's text spills outside it. Stack the
   title block full-width with the button and pills on a row below. */
@media (max-width: 575.98px) {
  .weekly-hours-header {
    flex-wrap: wrap;
    gap: 0.5rem 0.75rem;
    padding: 0.75rem 1rem;
  }

  .weekly-hours-header > div:first-child {
    flex: 1 1 100%;
    min-width: 0;
  }

  .weekly-hours-header-actions {
    flex: 1 1 100%;
    flex-wrap: wrap;
    gap: 0.5rem 0.75rem;
  }

  .weekly-hours-header .btn {
    flex: 0 0 auto;
    white-space: nowrap;
    height: auto;
    min-height: 0;
  }

  .weekly-hours-summary {
    flex: 1 1 auto;
    justify-content: center;
  }
}

.weekly-hours-summary span {
  padding: 0.35rem 0.65rem;
  border: 1px solid #dbeafe;
  border-radius: 999px;
  background: #ffffff;
  color: #1d4ed8;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
}

.weekly-hours-scroll {
  max-height: 68vh;
  background: #eef2f7;
  scrollbar-color: #94a3b8 #e2e8f0;
}

.hours-grid {
  min-width: 1610px;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  background: #f8fafc;
}

.hours-grid th,
.hours-grid td {
  font-size: 0.8rem;
  vertical-align: top;
  border-color: #e2e8f0;
}

.hours-grid thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 0.8rem 0.7rem;
  border-top: 0;
  border-bottom: 1px solid #cbd5e1;
  background: #ffffff;
  color: #334155;
  box-shadow: 0 1px 0 rgba(148, 163, 184, 0.25);
}

.worker-heading {
  left: 0;
  z-index: 4 !important;
  width: 245px;
  box-shadow: 1px 0 0 #cbd5e1;
}

// 180px, not 138px: DatetimeInput's type="time" fields carry a 148px
// min-width floor (Safari's native time control clips below it), plus this
// cell's own 0.55rem padding on each side — 138px was narrower than the
// input it has to hold, so the Start/Finish boxes overflowed into the next
// day's column. See SupervisorWorkerCards' .worker-mobile-days for the same
// fix in the mobile card layout.
.day-heading {
  width: 180px;
}

// Not position:sticky. A right-anchored sticky column docks at the wrap's
// visible edge the instant the table needs horizontal scroll, and that
// offset comes from the wrap's width, not from any day-column boundary — so
// it lands mid-column, not after the last one, whenever the 7-day grid
// scrolls (the common case). See worker-heading's left-sticky for contrast:
// it only pins once you scroll past it. Letting Total scroll like every
// other column removes the bug entirely.
.total-heading {
  width: 105px;
  box-shadow: -1px 0 0 #cbd5e1;
}

.day-label {
  display: block;
  font-weight: 700;
  line-height: 1.2;
}

.day-heading small {
  display: block;
  margin-top: 0.15rem;
  color: #64748b;
  font-weight: 500;
}

.worker-cell {
  position: sticky;
  left: 0;
  z-index: 1;
  padding: 1rem 0.9rem;
  background: #ffffff;
  box-shadow: 1px 0 0 #cbd5e1;
}

.worker-name {
  color: #0f172a;
  font-size: 0.86rem;
  font-weight: 700;
  line-height: 1.25;
}

.worker-trade {
  margin-top: 0.25rem;
  color: #64748b;
  font-size: 0.72rem;
  line-height: 1.3;
}

.day-cell {
  padding: 0.55rem;
  background: #ffffff;
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}

.day-cell-active {
  background: #f0fdf4;
  box-shadow: inset 0 0 0 1px #bbf7d0;
}

.day-cell-muted {
  background: #f1f5f9;
  color: #94a3b8;
}

.day-cell-leave {
  background: $leave-bg;
  box-shadow: inset 4px 0 0 $leave-border;
}

.leave-type-select {
  margin-bottom: 0.45rem;
  font-size: 0.72rem;
}

// A day the API won't accept any write on. Reads as leave, but visibly
// not yours to change.
.day-cell-locked {
  box-shadow: inset 4px 0 0 #64748b;

  .leave-type-select:disabled {
    background: #ffffff;
    color: #475569;
    cursor: not-allowed;
  }
}

.day-locked-note {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  max-width: 100%;
  padding: 0.22rem 0.4rem;
  border-radius: 999px;
  background: #e2e8f0;
  font-size: 0.66rem;
  font-weight: 600;
  line-height: 1;
  color: #475569;
  white-space: nowrap;
  cursor: help;

  .b-icon {
    flex-shrink: 0;
  }
}

.leave-day-badge {
  padding: 0.4rem 0.5rem;
  border-radius: 8px;
  background: #ffffff;
  color: $leave-text;
  font-size: 0.72rem;
  font-weight: 700;
  text-align: center;
}

.table-secondary .worker-cell,
.table-secondary .row-total-cell {
  background: #f1f5f9;
}

.day-availability {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  margin-bottom: 0.45rem;
  border: 1px solid #dbe3ef;
  border-radius: 10px;
  background: #f8fafc;
}

.day-availability-active {
  justify-content: space-between;
  gap: 0.45rem;
  padding: 0.25rem 0.35rem 0.25rem 0.6rem;
  border-color: #86efac;
  background: #dcfce7;
}

.day-add-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  width: 100%;
  min-height: 34px;
  padding: 0;
  border: 1px dashed #b6c3d1;
  border-radius: 10px;
  background: #ffffff;
  color: #475569;
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease,
    background-color 0.15s ease, box-shadow 0.15s ease;
}

.day-add-btn:hover {
  border-color: #60a5fa;
  color: #1d4ed8;
  background: #eff6ff;
  box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.12);
}

.day-remove-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: #ffffff;
  color: #15803d;
  cursor: pointer;
}

.day-remove-btn:hover {
  background: #fee2e2;
  color: #b91c1c;
}

.presence-pill {
  color: #15803d;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
}

.day-fields {
  display: flex;
  flex-direction: column;
  gap: 0.38rem;
}

.day-field {
  /* Label above the input (rather than beside it in a fixed 40px column) so
     the full width of the narrow day-cell is free for the value — a
     side-by-side label was squeezing 12-hour times ("10:45 AM") until the
     AM/PM suffix got clipped by the native control. */
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  margin: 0;
}

.day-field > span {
  color: #64748b;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.day-fields /deep/ .datetime-input,
.day-fields /deep/ .form-control {
  height: 32px;
  min-height: 32px;
  border-color: #cbd5e1;
  border-radius: 8px;
  color: #0f172a;
  font-size: 0.78rem;
  font-weight: 600;
}

.day-fields /deep/ .datetime-input {
  padding-right: 28px;
}

.day-fields /deep/ .datetime-icon {
  right: 8px;
  font-size: 12px;
}

.day-hours {
  margin-top: 0.1rem;
  padding: 0.35rem 0.45rem;
  border-radius: 8px;
  background: #ffffff;
  color: #166534;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1.25;
}

.day-overnight {
  margin-top: 0.15rem;
  color: #6b7280;
  font-size: 0.7rem;
}

.day-error {
  margin-top: 0.15rem;
  color: #b91c1c;
  font-size: 0.7rem;
}

.form-inline-error {
  margin: 0;
  align-self: center;
  color: #b91c1c;
  font-size: 0.8rem;
}

.offsite-mark {
  display: block;
  padding-top: 0.35rem;
  color: #94a3b8;
  font-size: 0.8rem;
  text-align: center;
}

.row-total-cell {
  padding: 0.9rem 0.6rem;
  color: #0f172a;
  font-size: 0.8rem;
  font-weight: 700;
  text-align: center;
  vertical-align: middle;
  background: #ffffff;
  box-shadow: -1px 0 0 #cbd5e1;
}

.row-total-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 64px;
  padding: 0.35rem 0.45rem;
  border-radius: 999px;
  background: #f1f5f9;
  color: #0f172a;
}

.hours-grid tbody tr:nth-child(even) .worker-cell,
.hours-grid tbody tr:nth-child(even) .day-cell,
.hours-grid tbody tr:nth-child(even) .row-total-cell {
  background: #fbfdff;
}

.hours-grid tbody tr:nth-child(even) .day-cell-active {
  background: #ecfdf5;
}

.hours-grid tbody tr:hover .worker-cell,
.hours-grid tbody tr:hover .day-cell,
.hours-grid tbody tr:hover .row-total-cell {
  background: #f8fbff;
}

.hours-grid tbody tr:hover .day-cell-active {
  background: #dcfce7;
}

.hours-grid /deep/ .custom-control {
  min-height: 1rem;
  padding-left: 1rem;
}

.hours-grid /deep/ .custom-control-label {
  min-height: 1rem;
  line-height: 1rem;
}

.hours-grid /deep/ .custom-control-label::before,
.hours-grid /deep/ .custom-control-label::after {
  top: 0;
  left: -1rem;
}

.gap-2 {
  gap: 0.5rem;
}

.declaration-check {
  margin-top: 0.75rem;
  line-height: 1.4;
}

@media (max-width: 767.98px) {
  .standard-hours-actions {
    align-items: stretch;
    margin-top: 0.75rem;
  }

  .standard-hours-actions .btn {
    flex: 1;
  }
}

/* Phones: "Comments / Notes" card gets tighter padding, and the declaration
   checkbox a bit more breathing room since its text now wraps to 3-4 lines
   in the narrow card. The action buttons below ("Submit for Approval",
   "Amend & Resubmit") are too long to sit side by side without wrapping or
   clipping, so stack them full-width instead. */
@media (max-width: 575.98px) {
  .comment-declaration-body {
    padding: 0.9rem 1rem;
  }

  .declaration-check {
    font-size: 0.85rem;
  }

  .submit-form-actions {
    flex-direction: column;
  }

  .submit-form-actions .btn {
    width: 100%;
    min-height: 44px;
  }
}

/* Phones: the 7-day grid is a 1260px-wide table with sticky Worker/Total
   columns that squashes to a sliver behind a horizontal scroll. Restack it:
   each worker becomes a block — name, then a labelled row per day, then
   their total — with no sideways scrolling. (This block must stay after the
   base .hours-grid rules above to win the cascade.) */
@media (max-width: 575.98px) {
  .weekly-hours-scroll {
    max-height: none;
    overflow-x: hidden;
  }

  .hours-grid {
    min-width: 0;
  }

  .hours-grid thead {
    display: none;
  }

  .hours-grid tbody,
  .hours-grid tfoot,
  .hours-grid tr,
  .hours-grid td {
    display: block;
    width: 100%;
  }

  .hours-grid tr {
    background: #ffffff;
    border-bottom: 6px solid #eef2f7;
  }

  .hours-grid td {
    border: 0;
  }

  .hours-grid .worker-cell,
  .hours-grid .row-total-cell {
    position: static;
    box-shadow: none;
  }

  .hours-grid .worker-cell {
    padding: 0.65rem 0.75rem 0.3rem;
  }

  .hours-grid .day-cell {
    display: grid;
    grid-template-columns: 44px minmax(0, 1fr);
    column-gap: 0.5rem;
    padding: 0.3rem 0.75rem;
  }

  .hours-grid .day-cell::before {
    content: attr(data-label);
    padding-top: 0.45rem;
    font-size: 0.75rem;
    font-weight: 700;
    color: #334155;
  }

  /* min-width:0 overrides the grid item's automatic minimum size, which
     otherwise defaults to the item's min-content width. That min-content
     bottoms out at DatetimeInput's 148px hard floor on type="time" inputs
     (see DatetimeInput.vue), so without this the "minmax(0, 1fr)" track
     above is a lie: the track gets forced to at least ~148px+padding
     regardless of how much room is actually available, and on a narrow
     phone that pushes Start/Finish past the card's right edge instead of
     just wrapping/filling the column. min-width:0 lets the column truly
     size to available space; the input can still scroll internally within
     .day-fields if the viewport is narrower than its 148px floor. */
  .hours-grid .day-cell .day-fields {
    grid-column: 2;
    min-width: 0;
  }

  .hours-grid .day-cell .day-fields /deep/ .datetime-field {
    max-width: 100%;
    overflow-x: auto;
  }

  /* .day-availability (the Add-day / "On site" pill) is a sibling of
     .day-fields, not nested inside it, so without its own grid-column it
     falls into CSS grid auto-placement and lands in the 44px label gutter —
     then stretches to fill that row's height (grid's default stretch
     alignment), rendering as a tall vertical strip instead of a normal
     pill. Pin it to the content column like .day-fields. */
  .hours-grid .day-cell .day-availability,
  .hours-grid .day-cell .day-locked-note,
  .hours-grid .day-cell .leave-day-badge {
    grid-column: 2;
    min-width: 0;
  }

  .hours-grid .day-cell .leave-type-select {
    grid-column: 2;
    min-width: 0;
  }

  .hours-grid .day-availability {
    margin-bottom: 0.3rem;
  }

  /* Off-site workers: skip their seven placeholder day rows entirely. */
  .hours-grid .day-cell-muted {
    display: none;
  }

  .hours-grid .row-total-cell {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.4rem 0.75rem 0.65rem;
  }

  .hours-grid .row-total-cell::before {
    content: 'Total';
    font-size: 0.75rem;
    font-weight: 700;
    color: #334155;
  }

  /* Footer day totals become labelled rows too. */
  .hours-grid tfoot .day-total-cell {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.3rem 0.75rem !important;
  }

  .hours-grid tfoot .day-total-cell::before {
    content: attr(data-label);
    font-size: 0.75rem;
    font-weight: 600;
    color: #334155;
  }
}

/* Submission photo thumbnails — the markup/lightbox reuse the pattern from
   SupervisorTimesheetTab, but its .event-photo* styles are scoped there, so
   redeclare them. */
.event-photos {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.event-photo-thumb {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: opacity 0.15s;
}

.event-photo-thumb:hover {
  opacity: 0.8;
}

.photo-thumb-wrap {
  position: relative;
  display: inline-block;
}

.photo-thumb-deleting {
  opacity: 0.6;
  pointer-events: none;
}

.remove-icon {
  position: absolute;
  top: 2px;
  right: 2px;
  cursor: pointer;
  background: #ffffff;
  border-radius: 999px;
}
</style>
